import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import {
  Box,
  Grid,
  TextField,
  CssBaseline,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Typography,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import CustomButton from "../../components/ui/CustomButton";
import { errorAlert, successAlert } from "../../utils/services";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import axiosInstance from "../../helpers/axios";

const validationSchema = Yup.object().shape({
  fullName: Yup.string()
    .required("Full Name is required")
    .min(3, "Full Name should be at least 3 characters"),
  email: Yup.string().required("Email is required").email("Email is invalid"),
  password: Yup.string()
    .required("Password is required")
    .min(6, "Password should be at least 6 characters"),
  passwordRepeat: Yup.string()
    .oneOf([Yup.ref("password"), null], "Passwords must match")
    .required("Confirm Password is required"),
});

const Signup = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleClickShowConfirmPassword = () =>
    setShowConfirmPassword(!showConfirmPassword);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const onSignUp = async (data) => {
    try {
      await axiosInstance.post(`/signup/admin`, data);
      successAlert("Account created successfully.");
      navigate("/");
    } catch (error) {
      errorAlert(
        error?.response?.data?.message || "Network error! Please try again."
      );
    }
  };

  return (
    <>
      <ToastContainer />
      <CssBaseline />
      <Grid container>
        {/* Left Image */}
        <Grid
          item
          md={6}
          justifyContent="center"
          alignItems="center"
          height="100vh"
          sx={{
            display: { xs: "none", md: "flex" },
            backgroundImage: `url(https://comput-images.blr1.cdn.digitaloceanspaces.com/login-bg-image.jpeg)`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        >
          <Typography
            variant="h3"
            align="center"
            color="white"
            fontWeight="600"
          >
            Create Admin Account
          </Typography>
        </Grid>

        {/* Right Form */}
        <Grid
          item
          md={6}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          height="100vh"
          padding={5}
          width="100%"
          gap="15px"
        >
          {/* Logo */}
          <Box
            justifyContent="center"
            alignItems="center"
            sx={{
              display: { md: "none", xs: "flex" },
            }}
          >
            <img
              src={`https://comput-images.blr1.cdn.digitaloceanspaces.com/Comput_labs_black.png`}
              alt="logo"
              style={{ width: "50%" }}
              loading="lazy"
            />
          </Box>

          <Typography variant="h5">Create Admin Account</Typography>

          {/* Form */}
          <Box
            component="form"
            onSubmit={handleSubmit(onSignUp)}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            width="100%"
          >
            <TextField
              name="fullName"
              fullWidth
              id="fullName"
              label="Full Name"
              margin="normal"
              {...register("fullName")}
              error={!!errors.fullName}
              helperText={errors.fullName?.message}
            />

            <TextField
              fullWidth
              variant="outlined"
              label="Email address"
              margin="normal"
              {...register("email")}
              error={!!errors.email}
              helperText={errors.email?.message}
            />

            <FormControl
              fullWidth
              variant="outlined"
              margin="normal"
              error={!!errors.password}
              className="formInput"
            >
              <InputLabel
                htmlFor="outlined-adornment-password"
                sx={{ color: errors.password ? "red" : "" }}
              >
                Password
              </InputLabel>
              <OutlinedInput
                id="outlined-adornment-password"
                type={showPassword ? "text" : "password"}
                {...register("password")}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                label="Password"
                className={errors.password ? "formInputError" : ""}
                sx={{
                  "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor: "red",
                    },
                }}
              />
              {errors.password && (
                <p className="errorMessage">{errors.password?.message}</p>
              )}
            </FormControl>

            <FormControl
              fullWidth
              variant="outlined"
              margin="normal"
              error={!!errors.passwordRepeat}
              className="formInput"
            >
              <InputLabel
                htmlFor="outlined-adornment-confirm-password"
                sx={{ color: errors.passwordRepeat ? "red" : "" }}
              >
                Confirm Password
              </InputLabel>
              <OutlinedInput
                id="outlined-adornment-confirm-password"
                type={showConfirmPassword ? "text" : "password"}
                {...register("passwordRepeat")}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowConfirmPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                label="Confirm Password"
                className={errors.passwordRepeat ? "formInputError" : ""}
                sx={{
                  "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor: "red",
                    },
                }}
              />
              {errors.passwordRepeat && (
                <p className="errorMessage">{errors.passwordRepeat?.message}</p>
              )}
            </FormControl>

            <CustomButton type="submit">Sign Up</CustomButton>
          </Box>

          {/* Extra Button */}
          <Box
            display="flex"
            justifyContent="center"
            width="100%"
            marginTop="10px"
          >
            <CustomButton
              variant="text"
              color="gray"
              onClick={() => navigate("/")}
            >
              Already have an account? Sign In
            </CustomButton>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default Signup;
